<template>
  <button
    ref="taskButtonEl"
    v-click-outside="clearAndBlur"
    class="task-button"
    :class="{ isEditing }"
    :tabindex="tabindex"
    @click="click"
  >
    <TaskIcon :recurring="Recurrence.Recurring" class="checkbox-recurring" />
    <BaseInput
      ref="inputEl"
      v-model:value="taskTitle"
      class="input"
      placeholder="Add recurring task"
      :textarea-default-rows="1"
      @focus="onFocus"
      @enter="openRecurrencePicker"
      @escape="clearAndBlur"
    />
  </button>
</template>

<script lang="ts">
import { isEmpty } from "lodash-es";
import { defineComponent, ref } from "vue";

import type { Recurrence as RecurrenceRule } from "@ctrl/controller-rpc";

import BaseInput from "@/components/base/BaseInput.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { Recurrence } from "@/components/recurrence";

export default defineComponent({
  name: "AddRecurringTaskButton",
  components: {
    BaseInput,
    TaskIcon,
  },
  props: {
    tabindex: { type: Number, default: 0 },
  },
  emits: {
    blur: () => true,
    focus: () => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const recurrencePicker = inject.recurrencePicker();

    const taskButtonEl = ref<HTMLElement>();
    const inputEl = ref<InstanceType<typeof BaseInput>>();
    const isEditing = ref(false);
    const taskTitle = ref("");

    const focus = () => {
      isEditing.value = true;
      inputEl.value?.focus();
    };
    const click = () => focus();
    const onFocus = () => emit("focus");

    const clearAndBlur = () => {
      taskTitle.value = "";
      isEditing.value = false;
      inputEl.value?.blur();
      emit("blur");
    };

    const openRecurrencePicker = async () => {
      if (isEmpty(taskTitle.value)) {
        clearAndBlur();
        return;
      }
      const title = taskTitle.value;
      const el = taskButtonEl.value;
      const rect = el?.getBoundingClientRect();
      await recurrencePicker.value.showPicker(
        el,
        null,
        async (recurrence: RecurrenceRule) => {
          await controller.taskRecurrent.create(
            {
              title,
              parent: null,
              recurrence,
            },
            { notify: true }
          );
          clearAndBlur();
        },
        "left",
        rect
      );
    };

    return {
      click,
      clearAndBlur,
      inputEl,
      isEditing,
      onFocus,
      openRecurrencePicker,
      Recurrence,
      taskButtonEl,
      taskTitle,
    };
  },
});
</script>

<style lang="scss" scoped>
.task-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  width: 100%;
  text-align: start;
  border-radius: 3px;
  min-height: 42px;
  padding: 6px 16px;
  column-gap: 8px;
  border: 1px solid transparent;

  @include themed() {
    caret-color: t($secondary);
    color: t($primary);
  }

  :deep(.base-input) .input {
    @extend %font-body-regular;
    @include themed() {
      color: t($primary);
    }
  }

  :deep(.textarea) {
    @extend %font-body-regular;
    margin-top: -1px;
  }

  :deep(input[type="text" i]) {
    padding: 0;
  }

  .checkbox-recurring {
    margin-top: 1px;
    flex-shrink: 0;
  }

  .input {
    width: inherit;
  }

  &:hover {
    cursor: text;
    @include themed() {
      border: 1px solid t($border-inline-element-hover);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
  }

  &.isEditing {
    @include themed() {
      background: t($bg-inline-element-editing);
      border: 1px solid t($bg-inline-element-editing);
    }

    :deep(.task-icon) .recurring {
      @include themed() {
        background: t($bg-inline-element-editing);
      }
    }
  }
}
</style>
